import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, mergeProps as _mergeProps, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "body-condition-graph-form" }
const _hoisted_2 = { class: "form-period-container" }
const _hoisted_3 = { class: "form-period-title" }
const _hoisted_4 = { class: "form-period-select" }
const _hoisted_5 = { class: "form-period-past-text" }
const _hoisted_6 = { class: "form-period-select-list" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "form-period-input" }
const _hoisted_9 = { class: "form-period-input-date-wrapper" }
const _hoisted_10 = ["min", "max"]
const _hoisted_11 = { class: "form-period-input-date-wrapper" }
const _hoisted_12 = ["min", "max"]
const _hoisted_13 = { class: "form-period-last-visit-date" }
const _hoisted_14 = { class: "form-period-last-visit-date-description" }
const _hoisted_15 = { class: "clinic-note-content-item-graph" }
const _hoisted_16 = { class: "modal-footer-buttons" }

import dayjs from "dayjs";
import "dayjs/locale/ja";

import {
  computed,
  watch,
  ref,
  reactive,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { processing, processend } from "@/utils/view";
import { RawItem } from "./types";
import { _$t } from "@/utils/i18n";
import { createMacSafariDatePickerCloser } from "@/utils/mac-safari-date-picker";

import ClinicNote from "@/models/myRecord/clinicNote/clinicNote";

import ConfirmDialog from "@/views/base/CommonMessage.vue";
import MessageDialog from "@/views/base/CommonMessage.vue";
import ErrorDialog from "@/views/base/CommonMessage.vue";
import Modal from "@/views/base/ModalComponent.vue";
import ConditionLineChart from "@/views/base/Chart/ConditionLineChart.vue";

// 日付のローカライズ
enum PeriodType {
  week = 1,
  month = 2,
  year = 3,
}

// --- Constants & Variables ------------------------------
const componentId = "healthcaredata02-01-g";

const DAY_FORMAT = "YYYY-MM-DD";


export default /*@__PURE__*/_defineComponent({
  __name: 'GraphSelector',
  props: {
    hospitalVisitId: {},
    bodyConditions: {},
    startDate: { default: "" },
    endDate: { default: "" },
    lastVisitDate: { default: "" }
  },
  emits: ["close", "save"],
  setup(__props: any, { emit: __emit }) {

dayjs.locale("ja");

// --- props ----------------------------------------
const props = __props;

const emit = __emit;

// --- Types ---------------------------------------------

const localStartDate = ref<string>(props.startDate);
const localEndDate = ref<string>(props.endDate);
const lastVisitDate = ref<string>(props.lastVisitDate);
const bodyConditions = ref<RawItem[]>(props.bodyConditions);
const startInput = ref<HTMLInputElement | null>(null);
const endInput = ref<HTMLInputElement | null>(null);
// macOS Safari 向け date picker クローズ処理
const safariDatePickerCloser = createMacSafariDatePickerCloser();

// 1週間 / 1ヵ月前 / 1年前 ボタン
const btnOptions = [
  { value: PeriodType.week, label: "1週間前", valueNum: 6, unit: "day" },
  { value: PeriodType.month, label: "1ヵ月前", valueNum: 29, unit: "day" },
  { value: PeriodType.year, label: "1年前", valueNum: 364, unit: "day" },
] as const;

// 体調ラベル
const titleCond = { show: false };

// ▼ トースト表示用
const { appContext } = getCurrentInstance()!;
const $toast = appContext.config.globalProperties.$toast;

// --- Dialogs -------------------------------------------

// 確認ダイアログ関連情報
let confirmDialogSet = reactive({
  show: false,
  okButtonHide: true,
  okButton: true,
  okName: _$t("clinic_note_condition_edit_button_ok"),
  cancelButton: true,
  cancelName: _$t("clinic_note_condition_edit_button_cancel"),
  message: "", // 表示メッセージ
  open: (message: string): void => {
    processing();
    confirmDialogSet.message = message;
    confirmDialogSet.show = true;
  },
  execute: async (): Promise<void> => {
    processend();
    confirmDialogSet.show = false;
    await save();
    // トースト表示
    $toast({
      // ▼ 表示メッセージ
      //   PCの場合はメッセージ内の<br>が消えます
      message: _$t("clinic_note_condition_edit_message_saved"),
      // ▼ 位置指定する場合は設定（単位: px）
      //   指定なしの場合
      //    【PC】表示位置
      //       X:画面左から56px
      //       Y:画面下から20px

      //    【SP】表示位置
      //       X:画面左から16px
      //       Y:画面下から84px（フッターナビから20px上）
      //
      // offsetX: 0,
      // offsetY: 0,
      // ▼ タイムアウト値を変える場合は設定（単位: ms）
      //   デフォルトは2500ms
      // timer: 30000,
      icon: "success",
    });
    // 診察ノートへ反映
    emit("save", localStartDate.value, localEndDate.value);
  },
  close: (): void => {
    processend();
    confirmDialogSet.show = false;
  },
});

// メッセージダイアログ関連情報
let messageDialogSet = reactive({
  show: false,
  okButton: true,
  okButtonHide: true,
  okName: _$t("clinic_note_condition_edit_button_ok"), // OK
  message: "",
  open: (message: string): void => {
    processing();
    messageDialogSet.message = message;
    messageDialogSet.show = true;
  },
  close: (): void => {
    processend();
    messageDialogSet.show = false;
    // 診察ノートへ反映
    emit("save", localStartDate.value, localEndDate.value);
  },
});

// エラーダイアログ関連情報
const sessionErr = ref(false);
let errorDialogSet = reactive({
  show: false,
  okButtonHide: true,
  safe: false,
  message: "",
  open: (message: string): void => {
    processing(); // オーバレイを表示
    errorDialogSet.message = message;
    errorDialogSet.show = true;
  },
  close: (): void => {
    processend(); // オーバレイを非表示
    errorDialogSet.show = false;
    errorDialogSet.message = "";
    if (sessionErr.value) {
      window.location.replace("/cms/login");
    }
  },
});

// --- Functions ---------------------------------------------

function onDateSelected(event: Event) {
  safariDatePickerCloser.onDateSelected(event);
}

// 日付整形
function formatDate(date: string) {
  return date ? dayjs(date).format("YYYY年M月D日(ddd)") : "";
}

function today() {
  // 選択可能な上限（今日）
  return dayjs().startOf("day");
}

function oldest() {
  // 選択可能な下限（1年前）
  return dayjs().subtract(1, "year").startOf("day");
}

function parseDate(value: string, fallback: dayjs.Dayjs) {
  // yyyy-mm-dd で厳密パース。無効値は fallback を採用
  const parsed = dayjs(value, DAY_FORMAT, true);
  return parsed.isValid() ? parsed.startOf("day") : fallback;
}

function clampDate(value: dayjs.Dayjs, min: dayjs.Dayjs, max: dayjs.Dayjs) {
  // [min, max] に収める共通関数
  if (value.isBefore(min)) return min;
  if (value.isAfter(max)) return max;
  return value;
}

function normalizeRange(changedBy: "start" | "end" | "init") {
  // 入力欄2つの共通制約:
  // 1) 全体範囲は「1年前〜今日」
  // 2) 開始日〜終了日は最低7日間（6日差）
  const min = oldest();
  const max = today();
  const minSpanDays = 6;

  let start = clampDate(parseDate(localStartDate.value, min), min, max);
  let end = clampDate(parseDate(localEndDate.value, max), min, max);

  // 期間下限: 開始〜終了は最低7日（6日差）
  if (start.isAfter(end.subtract(minSpanDays, "day"))) {
    if (changedBy === "start") {
      end = start.add(minSpanDays, "day");
    } else {
      start = end.subtract(minSpanDays, "day");
    }
  }

  start = clampDate(start, min, max);
  end = clampDate(end, min, max);

  const nextStart = start.format(DAY_FORMAT);
  const nextEnd = end.format(DAY_FORMAT);
  // 同値代入を避けて不要な再描画/監視連鎖を防ぐ
  if (localStartDate.value !== nextStart) localStartDate.value = nextStart;
  if (localEndDate.value !== nextEnd) localEndDate.value = nextEnd;
}

// 取得
async function get() {
  // 診察ノートモデル作成
  const clinicNoteModel = new ClinicNote();
  // パラメータ設定
  const params = {
    startDate: localStartDate.value,
    endDate: localEndDate.value,
  };
  // 体調の取得
  const [_model, response, err] = await clinicNoteModel.getConditionData(
    params
  );

  // エラー判定
  if (err) {
    const status = err.error.response.status;
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    // セッションエラー
    if (status === 408) sessionErr.value = true;
    errorDialogSet.open(decodedMessage);
    return;
  }
  // データ更新
  bodyConditions.value = response.data;
}

// 保存処理
async function save() {
  // 表示期間の設定
  const clinicNoteModel = new ClinicNote();
  const params = {
    startDate: localStartDate.value,
    endDate: localEndDate.value,
    hospitalVisitId: props.hospitalVisitId,
  };
  const [_model, response, err] = await clinicNoteModel.setSpan(params);
  // エラー判定
  if (err) {
    const status = err.error.response.status;
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    // セッションエラー
    if (status === 408) sessionErr.value = true;
    errorDialogSet.open(decodedMessage);
    return;
  }
  // データ更新
  localStartDate.value = response.data.startDate;
  localEndDate.value = response.data.endDate;

  // 完了メッセージ
  messageDialogSet.open(_$t("clinic_note_condition_edit_message_saved"));
}

// --- Event Handlers -------------------------------------------

// 保存
async function onSave() {
  await save();
  // トースト表示
  $toast({
    // ▼ 表示メッセージ
    //   PCの場合はメッセージ内の<br>が消えます
    message: _$t("clinic_note_condition_edit_message_saved"),
    // ▼ 位置指定する場合は設定（単位: px）
    //   指定なしの場合
    //    【PC】表示位置
    //       X:画面左から56px
    //       Y:画面下から20px

    //    【SP】表示位置
    //       X:画面左から16px
    //       Y:画面下から84px（フッターナビから20px上）
    //
    // offsetX: 0,
    // offsetY: 0,
    // ▼ タイムアウト値を変える場合は設定（単位: ms）
    //   デフォルトは2500ms
    // timer: 30000,
    icon: "success",
  });
  // 診察ノートへ反映
  emit("save", localStartDate.value, localEndDate.value);
}

// モーダルを閉じる
function onClose() {
  emit("close");
}

// 1週間前 / 1カ月前 / 1年前 選択
function onClick(periodType: PeriodType) {
  const period = btnOptions.find((p) => p.value === periodType);
  if (!period) return;
  localStartDate.value = dayjs()
    .subtract(period.valueNum, period.unit)
    .format("YYYY-MM-DD");
  localEndDate.value = dayjs().format("YYYY-MM-DD");
}

// --- Vue lifecycle hooks ----------------------------------
const formattedStartDate = computed(() => formatDate(localStartDate.value));
const formattedEndDate = computed(() => formatDate(localEndDate.value));
const fomattedLastDate = computed(() => formatDate(lastVisitDate.value));
const startMinDate = computed(() => oldest().format(DAY_FORMAT));
const startMaxDate = computed(() => {
  // 終了日から見て、開始日の最大は「終了日の6日前」
  const min = oldest();
  const max = today();
  const end = parseDate(localEndDate.value, max);
  return clampDate(end.subtract(6, "day"), min, max).format(DAY_FORMAT);
});
const endMinDate = computed(() => {
  // 開始日から見て、終了日の最小は「開始日の6日後」
  const min = oldest();
  const max = today();
  const start = parseDate(localStartDate.value, min);
  return clampDate(start.add(6, "day"), min, max).format(DAY_FORMAT);
});
const endMaxDate = computed(() => today().format(DAY_FORMAT));

onMounted(() => {
  safariDatePickerCloser.enableSafariDateInputClass();
});

onBeforeUnmount(() => {
  safariDatePickerCloser.disableSafariDateInputClass();
});

normalizeRange("init");
// 日付を直接入力された場合でも、都度制約内に自動補正する
watch(localStartDate, () => normalizeRange("start"));
watch(localEndDate, () => normalizeRange("end"));
watch([localStartDate, localEndDate], async () => await get());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, {
      id: componentId,
      title: _ctx.$t('clinic_note_condition_edit_title_graph'),
      showTab: false,
      modalClass: "modal-body-condition-graph-edit",
      onClose: onClose
    }, {
      bodyInfo: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("clinic_note_condition_edit_title_period")), 1),
            _createElementVNode("div", _hoisted_4, [
              _cache[2] || (_cache[2] = _createElementVNode("img", { src: "/server/necs/images/icon_condition_select_today.svg" }, null, -1)),
              _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t("clinic_note_condition_edit_lasbel_past_text")), 1),
              _createElementVNode("ul", _hoisted_6, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(btnOptions, (item) => {
                  return _createElementVNode("li", {
                    key: item.value
                  }, [
                    _createElementVNode("button", {
                      class: "form-period-button",
                      onClick: ($event: any) => (onClick(item.value))
                    }, _toDisplayString(item.label), 9, _hoisted_7)
                  ])
                }), 64))
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _withDirectives(_createElementVNode("input", {
                  ref_key: "startInput",
                  ref: startInput,
                  type: "date",
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localStartDate).value = $event)),
                  onChange: onDateSelected,
                  onInput: onDateSelected,
                  min: startMinDate.value,
                  max: startMaxDate.value,
                  class: _normalizeClass([
                  'form-period-input-date',
                  { 'has-value': !!localStartDate.value },
                ])
                }, null, 42, _hoisted_10), [
                  [_vModelText, localStartDate.value]
                ]),
                _createElementVNode("span", {
                  class: "form-period-placeholder",
                  style: _normalizeStyle({ display: localStartDate.value ? 'none' : 'inline' })
                }, _toDisplayString(_ctx.$t("clinic_note_condition_edit_placeholder_period")), 5),
                _createElementVNode("span", {
                  class: "form-period-formatted-date",
                  style: _normalizeStyle({ display: localStartDate.value ? 'inline' : 'none' })
                }, _toDisplayString(formattedStartDate.value), 5)
              ]),
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "form-period-input-date-separator" }, "～", -1)),
              _createElementVNode("div", _hoisted_11, [
                _withDirectives(_createElementVNode("input", {
                  ref_key: "endInput",
                  ref: endInput,
                  type: "date",
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localEndDate).value = $event)),
                  onChange: onDateSelected,
                  onInput: onDateSelected,
                  min: endMinDate.value,
                  max: endMaxDate.value,
                  class: _normalizeClass([
                  'form-period-input-date',
                  { 'has-value': !!localEndDate.value },
                ])
                }, null, 42, _hoisted_12), [
                  [_vModelText, localEndDate.value]
                ]),
                _createElementVNode("span", {
                  class: "form-period-placeholder",
                  style: _normalizeStyle({ display: localEndDate.value ? 'none' : 'inline' })
                }, _toDisplayString(_ctx.$t("clinic_note_condition_edit_placeholder_period")), 5),
                _createElementVNode("span", {
                  class: "form-period-formatted-date",
                  style: _normalizeStyle({ display: localEndDate.value ? 'inline' : 'none' })
                }, _toDisplayString(formattedEndDate.value), 5)
              ])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("p", _hoisted_13, [
                _createTextVNode(_toDisplayString(_ctx.$t("clinic_note_condition_edit_label_last_visit_date")), 1),
                _createElementVNode("span", null, _toDisplayString(fomattedLastDate.value), 1)
              ]),
              _createElementVNode("p", _hoisted_14, _toDisplayString(_ctx.$t("clinic_note_condition_edit_description_last_visit_date")), 1)
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createVNode(ConditionLineChart, {
                bodyConditions: bodyConditions.value,
                pointSize: 8,
                startDate: localStartDate.value,
                endDate: localEndDate.value,
                titleCond: titleCond,
                class: "condition-line-chart"
              }, null, 8, ["bodyConditions", "startDate", "endDate"])
            ])
          ])
        ])
      ]),
      footerButton: _withCtx(() => [
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("button", {
            type: "button",
            class: "modal-cansel",
            onClick: onClose
          }, _toDisplayString(_ctx.$t("clinic_note_condition_edit_button_cancel")), 1),
          _createElementVNode("button", {
            type: "button",
            class: "modal-ok",
            onClick: onSave
          }, _toDisplayString(_ctx.$t("clinic_note_condition_edit_button_save")), 1)
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    (_unref(confirmDialogSet).show)
      ? (_openBlock(), _createBlock(ConfirmDialog, _mergeProps({ key: 0 }, _unref(confirmDialogSet), {
          onOk: _unref(confirmDialogSet).execute,
          onCancel: _unref(confirmDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true),
    (_unref(messageDialogSet).show)
      ? (_openBlock(), _createBlock(MessageDialog, _mergeProps({ key: 1 }, _unref(messageDialogSet), {
          onOk: _unref(messageDialogSet).close,
          onCancel: _unref(messageDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true),
    (_unref(errorDialogSet).show)
      ? (_openBlock(), _createBlock(ErrorDialog, _mergeProps({ key: 2 }, _unref(errorDialogSet), {
          onOk: _unref(errorDialogSet).close,
          onCancel: _unref(errorDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})